import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from "../../Context/Cartcontext";
import { useUser } from "@clerk/clerk-react";
import { useNav } from "../../Context/NavContext";
import './Checkout.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe('pk_test_51RgKvCSGYk9tM2yGnFhsGDw5dss9pjJFPZKbFqnBq2znCTbbKJ0OZTW8lC9DvQabF3GaH9GSVaLLlFRitnUFnFlB00A5XQdL7G');

const EnhancedStripePaymentForm = ({ finalTotal, onSuccess, customerInfo }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardFocused, setCardFocused] = useState(false);
  const [cardBrand, setCardBrand] = useState('unknown');

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSmoothing: 'antialiased',
        '::placeholder': {
          color: 'rgba(255, 255, 255, 0.5)',
        },
        iconColor: '#38b2ac',
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
      complete: {
        color: '#38b2ac',
        iconColor: '#38b2ac',
      },
    },
    hidePostalCode: true,
  };

  const handleCardChange = (event) => {
    setError(event.error ? event.error.message : null);
    setCardComplete(event.complete);
    setCardBrand(event.brand || 'unknown');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    if (!cardComplete) {
      setError('Please complete your card details');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const res = await fetch('http://propertecknew-production.up.railway.app/api/payment/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: finalTotal * 100,
          currency: 'lkr',
          customerInfo,
        }),
      });
      
      if (!res.ok) {
        throw new Error('Failed to create payment intent');
      }
      
      const { clientSecret } = await res.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else {
        if (result.paymentIntent.status === 'succeeded') {
          onSuccess();
        }
      }
    } catch (err) {
      setError('Payment failed. Please try again.');
      console.error('Payment error:', err);
    } finally {
      setProcessing(false);
    }
  };

  const getCardIcon = (brand) => {
    const icons = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳',
      diners: '💳',
      jcb: '💳',
      unionpay: '💳',
      unknown: '💳'
    };
    return icons[brand] || icons.unknown;
  };

  return (
    <div className="enhanced-stripe-form">
      <form onSubmit={handleSubmit}>
        <div className="card-input-section">
          <div className="card-input-header">
            <div className="accepted-cards">
              <span className="accepted-label">We accept:</span>
              <div className="card-brands">
                <div className="card-brand visa">
                  <span>VISA</span>
                </div>
                <div className="card-brand mastercard">
                  <span>MC</span>
                </div>
                <div className="card-brand amex">
                  <span>AMEX</span>
                </div>
                <div className="card-brand discover">
                  <span>DISC</span>
                </div>
              </div>
            </div>
            <div className="secure-badge">
              <span className="secure-icon">🔒</span>
              <span className="secure-text">256-bit SSL</span>
            </div>
          </div>

          <div className={`card-input-wrapper ${cardFocused ? 'focused' : ''} ${cardComplete ? 'complete' : ''} ${error ? 'error' : ''}`}>
            <div className="card-visual">
              <div className="card-mockup">
                <div className="card-chip"></div>
                <div className="card-brand-icon">
                  {getCardIcon(cardBrand)}
                </div>
                <div className="card-number-placeholder">
                  {cardComplete ? '•••• •••• •••• ••••' : '1234 5678 9012 3456'}
                </div>
                <div className="card-details">
                  <div className="card-holder">CARD HOLDER</div>
                  <div className="card-expiry">MM/YY</div>
                </div>
              </div>
            </div>

            <div className="card-form-container">
              <label className="card-label">
                <span className="label-text">Card Information</span>
                <span className="required-indicator">*</span>
              </label>
              
              <div className="card-element-container">
                <CardElement
                  options={cardElementOptions}
                  onChange={handleCardChange}
                  onFocus={() => setCardFocused(true)}
                  onBlur={() => setCardFocused(false)}
                />
              </div>

              <div className="card-security-info">
                <div className="security-item">
                  <span className="security-icon">🛡️</span>
                  <span className="security-text">Bank-level security</span>
                </div>
                <div className="security-item">
                  <span className="security-icon">🔐</span>
                  <span className="security-text">Data encrypted</span>
                </div>
                <div className="security-item">
                  <span className="security-icon">✅</span>
                  <span className="security-text">PCI compliant</span>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="payment-error-container">
              <div className="payment-error">
                <span className="error-icon">⚠️</span>
                <span className="error-message">{error}</span>
              </div>
            </div>
          )}
        </div>

        <div className="payment-summary-card">
          <div className="summary-header">
            <h4>Payment Summary</h4>
          </div>
          <div className="summary-content">
            <div className="amount-row">
              <span className="amount-label">Total Amount:</span>
              <span className="amount-value">{finalTotal.toLocaleString()} LKR</span>
            </div>
            <div className="currency-note">
              <span className="note-icon">ℹ️</span>
              <span className="note-text">Charged in Sri Lankan Rupees (LKR)</span>
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          disabled={!stripe || processing || !cardComplete} 
          className={`enhanced-payment-btn ${processing ? 'processing' : ''} ${!cardComplete ? 'disabled' : ''}`}
        >
          <div className="btn-content">
            {processing ? (
              <>
                <div className="processing-spinner"></div>
                <span>Processing Payment...</span>
              </>
            ) : (
              <>
                <span className="payment-icon">💳</span>
                <span>Pay {finalTotal.toLocaleString()} LKR Securely</span>
              </>
            )}
          </div>
        </button>

        <div className="trust-indicators">
          <div className="trust-item">
            <span className="trust-icon">🔒</span>
            <span className="trust-text">Secured by Stripe</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon">⚡</span>
            <span className="trust-text">Instant processing</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon">↩️</span>
            <span className="trust-text">Easy refunds</span>
          </div>
        </div>

        <p className="payment-disclaimer">
          Your payment information is encrypted and secure. You will receive a confirmation email after successful payment.
        </p>
      </form>
    </div>
  );
};

const Checkout = () => {
  const { cartItems, clearCart } = useCart();
  const { user, isLoaded, isSignedIn } = useUser();
  const { isAuthenticated, userInfo } = useNav();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    userType: 'personal',
    fullName: '',
    companyName: '',
    vatNumber: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    paymentMethod: 'card',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);

  const total = cartItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  const deliveryFee = 500;
  const finalTotal = total + deliveryFee;

  useEffect(() => {
    if (isLoaded && isSignedIn && user) {
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim() || 'User';
      setFormData((prev) => ({
        ...prev,
        fullName: fullName,
        email: user.primaryEmailAddress?.emailAddress || '',
      }));
    } else if (isAuthenticated && userInfo) {
      setFormData((prev) => ({
        ...prev,
        fullName: userInfo.name || '',
        email: userInfo.email || '',
      }));
    }
  }, [isLoaded, isSignedIn, user, isAuthenticated, userInfo]);

  useEffect(() => {
    if (!location.state?.fromCart) {
      navigate('/');
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (orderSuccess) {
      const timer = setTimeout(() => {
        navigate('/');
        window.location.reload();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [orderSuccess, navigate]);

  const validateForm = () => {
    const errors = {};

    if (formData.userType === 'personal') {
      if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
    } else {
      if (!formData.companyName.trim()) errors.companyName = 'Company name is required';
    }

    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) errors.phone = 'Phone must be 10 digits';
    if (!formData.address.trim()) errors.address = 'Address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.postalCode.trim()) errors.postalCode = 'Postal code is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleNext = () => {
    if (validateForm()) {
      setCurrentStep(2);
    }
  };

  const createOrder = async () => {
    try {
      const orderData = {
        customerInfo: {
          userType: formData.userType,
          fullName: formData.fullName,
          companyName: formData.companyName,
          vatNumber: formData.vatNumber,
          email: formData.email,
          phone: formData.phone.replace(/\D/g, ''),
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
        },
        items: cartItems.map((item) => ({
          productId: item._id || item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity || 1,
          imageUrl: item.imageUrl,
        })),
        paymentMethod: formData.paymentMethod,
        subtotal: total,
        deliveryFee: deliveryFee,
        total: finalTotal,
        paymentStatus: formData.paymentMethod === 'cod' ? 'pending' : 'pending',
        orderStatus: 'pending',
      };

      const response = await fetch('http://propertecknew-production.up.railway.app/api/order/createorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      const result = await response.json();

      if (response.ok) {
        return { success: true, data: result };
      } else {
        return { success: false, error: result.message || 'Failed to create order' };
      }
    } catch (error) {
      console.error('Error creating order:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const handleStripeSuccess = async () => {
    const result = await createOrder();
    if (result.success) {
      setOrderSuccess(result.data);
      if (clearCart) {
        clearCart();
      }
    } else {
      alert(`Order failed: ${result.error}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const result = await createOrder();

      if (result.success) {
        setOrderSuccess(result.data);
        if (clearCart) {
          clearCart();
        }
      } else {
        alert(`Order failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Shipping Info', icon: '📋' },
    { number: 2, title: 'Payment', icon: '💳' },
  ];

  if (orderSuccess) {
    return (
      <div className="checkout-container">
        <div className="checkout-wrapper">
          <div className="success-screen">
            <div className="success-icon">✓</div>
            <h1>Order Successful!</h1>
            <p>Thank you for your order. Confirmation email is on its way.</p>
            <p>Redirecting to home page in 3 seconds...</p>
            <button
              onClick={() => {
                navigate('/');
                window.location.reload();
              }}
              className="check-btn-primary"
            >
              Shop More
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-wrapper">
        <div className="checkout-header">
          <h1 className="checkout-title">Checkout</h1>
          <p className="checkout-subtitle">Finalize your purchase</p>
        </div>

        <div className="progress-container">
          {steps.map((step, index) => (
            <div key={step.number} className="progress-step-wrapper">
              <div className={`progress-step ${currentStep >= step.number ? 'active' : ''}`}>
                <span className="step-icon">{step.icon}</span>
                <span className="step-title">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`progress-line ${currentStep > step.number ? 'active' : ''}`} />
              )}
            </div>
          ))}
        </div>

        <div className="checkout-content">
          <div className="checkout-form-section">
            {currentStep === 1 ? (
              <div>
                <h2 className="section-title">📋 Shipping Details</h2>
                <div className="check-form-grid">
                  <div className="check-form-group">
                    <label className="check-form-label">Account Type</label>
                    <div className="check-account-type-selector">
                      <label className={`check-account-type-option ${formData.userType === 'personal' ? 'check-selected' : ''}`}>
                        <input 
                          type="radio" 
                          name="userType" 
                          value="personal" 
                          checked={formData.userType === 'personal'} 
                          onChange={handleChange} 
                        />
                        <span className="check-payment-icon">👤</span>
                        <span className="check-payment-label">Personal</span>
                      </label>
                      <label className={`check-account-type-option ${formData.userType === 'company' ? 'check-selected' : ''}`}>
                        <input 
                          type="radio" 
                          name="userType" 
                          value="company" 
                          checked={formData.userType === 'company'} 
                          onChange={handleChange} 
                        />
                        <span className="check-payment-icon">🏢</span>
                        <span className="check-payment-label">Company</span>
                      </label>
                    </div>
                  </div>

                  {formData.userType === 'personal' && (
                    <div className="check-form-group">
                      <label className="check-form-label">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className={`check-form-input ${formErrors.fullName ? 'check-error' : ''}`}
                      />
                      {formErrors.fullName && (
                        <span className="check-error-message">{formErrors.fullName}</span>
                      )}
                    </div>
                  )}

                  {formData.userType === 'company' && (
                    <>
                      <div className="check-form-group">
                        <label className="check-form-label">Company Name *</label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          className={`check-form-input ${formErrors.companyName ? 'check-error' : ''}`}
                        />
                        {formErrors.companyName && (
                          <span className="check-error-message">{formErrors.companyName}</span>
                        )}
                      </div>
                      <div className="check-form-group">
                        <label className="check-form-label">VAT Number (Optional)</label>
                        <input
                          type="text"
                          name="vatNumber"
                          value={formData.vatNumber}
                          onChange={handleChange}
                          className={`check-form-input ${formErrors.vatNumber ? 'check-error' : ''}`}
                        />
                        {formErrors.vatNumber && (
                          <span className="check-error-message">{formErrors.vatNumber}</span>
                        )}
                      </div>
                    </>
                  )}

                  <div className="check-form-row">
                    <div className="check-form-group">
                      <label className="check-form-label">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`check-form-input ${formErrors.email ? 'check-error' : ''}`}
                      />
                      {formErrors.email && (
                        <span className="check-error-message">{formErrors.email}</span>
                      )}
                    </div>

                    <div className="check-form-group">
                      <label className="check-form-label">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="0771234567"
                        className={`check-form-input ${formErrors.phone ? 'check-error' : ''}`}
                      />
                      {formErrors.phone && (
                        <span className="check-error-message">{formErrors.phone}</span>
                      )}
                    </div>
                  </div>

                  <div className="check-form-group">
                    <label className="check-form-label">Address *</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Enter full address..."
                      className={`check-form-textarea ${formErrors.address ? 'check-error' : ''}`}
                    />
                    {formErrors.address && (
                      <span className="check-error-message">{formErrors.address}</span>
                    )}
                  </div>

                  <div className="check-form-row">
                    <div className="check-form-group">
                      <label className="check-form-label">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`check-form-input ${formErrors.city ? 'check-error' : ''}`}
                      />
                      {formErrors.city && (
                        <span className="check-error-message">{formErrors.city}</span>
                      )}
                    </div>

                    <div className="check-form-group">
                      <label className="check-form-label">Postal Code *</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleChange}
                        placeholder="10400"
                        className={`check-form-input ${formErrors.postalCode ? 'check-error' : ''}`}
                      />
                      {formErrors.postalCode && (
                        <span className="check-error-message">{formErrors.postalCode}</span>
                      )}
                    </div>
                  </div>

                  <button type="button" onClick={handleNext} className="check-btn-primary check-btn-full">
                    Proceed to Payment →
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="section-title">💳 Payment Options</h2>
                <div className="payment-methods">
                  {[
                    { value: 'card', label: 'Credit/Debit Card', icon: '💳' },
                    { value: 'bank', label: 'Bank Transfer', icon: '🏦' },
                    { value: 'cod', label: 'Cash on Delivery', icon: '💵' },
                  ].map((method) => (
                    <label
                      key={method.value}
                      className={`payment-method ${formData.paymentMethod === method.value ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.value}
                        checked={formData.paymentMethod === method.value}
                        onChange={handleChange}
                      />
                      <span className="payment-icon">{method.icon}</span>
                      <span className="payment-label">{method.label}</span>
                    </label>
                  ))}
                </div>
                {formData.paymentMethod === 'card' ? (
                  <Elements stripe={stripePromise}>
                    <EnhancedStripePaymentForm
                      finalTotal={finalTotal}
                      onSuccess={handleStripeSuccess}
                      customerInfo={formData}
                    />
                  </Elements>
                ) : (
                  <div className="button-group">
                    <button type="button" onClick={() => setCurrentStep(1)} className="check-btn-secondary">
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className={`check-btn-primary ${isSubmitting ? 'loading' : ''}`}
                    >
                      {isSubmitting ? '🔄 Processing...' : `Place Order (${finalTotal.toLocaleString()} LKR)`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="order-summary-section">
            <h3 className="summary-title">🛍 Order Summary</h3>
            <div className="order-items">
              {cartItems.map((item) => (
                <div key={item.id || item._id} className="order-item">
                  <div className="item-image">
                    <img src={`http://propertecknew-production.up.railway.app/images/${item.imageUrl}`} alt={item.name} />
                  </div>
                  <div className="item-info">
                    <h4 className="item-name">{item.name}</h4>
                    <p className="item-quantity">Qty: {item.quantity || 1}</p>
                    <p className="item-price">{(item.price * (item.quantity || 1)).toLocaleString()} LKR</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="order-totals">
              <div className="total-row">
                <span>Subtotal:</span>
                <span>{total.toLocaleString()} LKR</span>
              </div>
              <div className="total-row">
                <span>Delivery:</span>
                <span>{deliveryFee.toLocaleString()} LKR</span>
              </div>
              <div className="total-final">
                <span>Total:</span>
                <span>{finalTotal.toLocaleString()} LKR</span>
              </div>
            </div>
            <div className="security-badge">
              <p>🔒 Secured with SSL encryption</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;